import { useSelector } from 'react-redux';
import { clientNameSelector } from '../../../../redux/auth/selectors';
import { CustomColumn } from '../../../../types/components/tables/tableTypes';
import {
  percentageToTwoDecimalPlaces,
  percentageToTwoDecimalPlacesNoHundred,
} from '../../../../utilities/numberFormatters';
import GridItem from '../../../layout/GridComponents/GridItem';
import CustomTable from '../../../tables/CustomTable';
import {
  SubRedPanelView,
  SubscriptionsRedemptionsReport,
} from './subscriptionsRedemptions.types';
import { SubRedPanelHeader } from './SubRedViewToggle';
import {
  PANEL_CARD_STYLE,
  resolveMetrics,
  resolveStatisticalData,
  SUB_RED_PANEL_CONFIG,
} from './utils/subRedPanelViews';

interface subscriptionsRedemptionsTableProps<T extends SubRedPanelView> {
  data: SubscriptionsRedemptionsReport;
  view: T;
  views: T[];
  onViewChange: (view: T) => void;
}

interface IsubscriptionsRedemptionsData {
  metric: string;
  scenario: string;
  value: number;
  M1: number;
  coverageRatio: number;
}

function buildSubscriptionsRedemptionData(
  data: SubscriptionsRedemptionsReport,
  view: SubRedPanelView,
): IsubscriptionsRedemptionsData[] {
  const tableData: IsubscriptionsRedemptionsData[] = [];
  const statisticalData = resolveStatisticalData(data, view);
  const metrics = resolveMetrics(data, view);
  const m1 = data.moab_data2?.top_level_data?.all_currency?.[1] ?? 0;

  for (let index = 0; index < statisticalData.length; index++) {
    const value = statisticalData[index];
    const coverageRatio = value === 0 ? 0 : m1 / (value * 100);
    tableData.push({
      metric: metrics[index] ?? data.statistical_metric?.[index] ?? '',
      scenario: data.statistical_scenario?.[index] ?? '',
      value,
      M1: m1,
      coverageRatio: Math.abs(coverageRatio),
    });
  }

  return tableData;
}

function buildTableColumns(
  clientName: string,
): CustomColumn<IsubscriptionsRedemptionsData>[] {
  const detailColumns: CustomColumn<IsubscriptionsRedemptionsData>[] = [
    {
      title: 'Metric',
      field: 'metric',
      cellStyle: { textAlign: 'center' },
      headerStyle: { textAlign: 'center' },
    },
    {
      title: 'Scenario',
      field: 'scenario',
      cellStyle: { textAlign: 'center' },
      headerStyle: { textAlign: 'center' },
    },
    {
      title: 'Value',
      field: 'value',
      render: (rowData) => percentageToTwoDecimalPlaces(rowData.value),
      cellStyle: { textAlign: 'center' },
      headerStyle: { textAlign: 'center' },
    },
    clientName !== 'gem'
      ? {
          title: 'M1',
          field: 'M1',
          render: (rowData) =>
            percentageToTwoDecimalPlacesNoHundred(rowData.M1),
          cellStyle: { textAlign: 'center' },
          headerStyle: { textAlign: 'center' },
        }
      : {
          width: '0%',
        },
    clientName !== 'gem'
      ? {
          title: 'Coverage Ratio',
          field: 'coverageRatio',
          render: (rowData) =>
            percentageToTwoDecimalPlacesNoHundred(rowData.coverageRatio),
          cellStyle: { textAlign: 'center' },
          headerStyle: { textAlign: 'center' },
        }
      : {
          width: '0%',
        },
  ];
  return detailColumns;
}

function SubscriptionsRedemptionsTable<T extends SubRedPanelView>({
  data,
  view,
  views,
  onViewChange,
}: subscriptionsRedemptionsTableProps<T>) {
  const config = SUB_RED_PANEL_CONFIG[view];
  const title = config.tableTitle;

  const clientName = useSelector(clientNameSelector) || 'mersenne';
  const tableData = buildSubscriptionsRedemptionData(data, view);
  const detailColumns = buildTableColumns(clientName);

  return (
    <GridItem
      xs={6}
      card
      cardStyle={PANEL_CARD_STYLE}
      style={{ alignSelf: 'stretch' }}
    >
      <SubRedPanelHeader
        title={title}
        view={view}
        views={views}
        onViewChange={onViewChange}
      />
      <CustomTable<IsubscriptionsRedemptionsData>
        pdfTitle={title}
        id={config.tableId}
        columns={detailColumns}
        showToolbar
        data={tableData}
        options={{
          paging: false,
          search: false,
          exportButton: true,
        }}
      />
    </GridItem>
  );
}

export default SubscriptionsRedemptionsTable;
